/**
 * Which app is this: the desktop app (Tauri) or the web app (web.apiboo.com)?
 *
 * One answer for the whole UI, instead of scattered `isTauri()` checks deciding
 * what a browser user may see. Resolution order:
 *   1. a test override (`setWebAppOverride`)
 *   2. the build target: `REACT_APP_TARGET=web` / `=desktop`
 *   3. under Jest → desktop (the Tauri mock says "not Tauri", and every existing
 *      suite was written against the desktop app)
 *   4. otherwise: a plain browser is the web app; `tauri dev` loading the same
 *      localhost:3000 inside the webview is the desktop app.
 */
import { isTauri } from '../utils/tauri';

let override = null;

export function isWebApp() {
  if (override !== null) return override;
  const target = process.env.REACT_APP_TARGET;
  if (target === 'web') return true;
  if (target === 'desktop') return false;
  if (process.env.NODE_ENV === 'test') return false;
  return !isTauri();
}

/** Tests only: force web (true) / desktop (false), or `null` to resolve normally. */
export function setWebAppOverride(value) {
  override = value === null ? null : Boolean(value);
}

/**
 * Devtools that need the local machine (sockets, files, native processes) and
 * therefore do not exist on the web: the API client (collections + history),
 * DB Manager, Terminal, FTP/SFTP and the Mail catcher.
 */
export const DESKTOP_ONLY_ACTIVITY_IDS = Object.freeze(
  new Set(['collections', 'history', 'git', 'db-manager.activity', 'terminal.activity', 'ftp.activity', 'mail-catcher.activity'])
);

export const DESKTOP_ONLY_PANELS = Object.freeze(
  new Set(['collections', 'history', 'git', 'db-manager.panel', 'terminal.panel', 'ftp.panel', 'mail-catcher.panel'])
);

export function isDesktopOnlyPanel(panelId) {
  return DESKTOP_ONLY_PANELS.has(panelId);
}

/** Where the web app lands when nothing (or a desktop-only page) is open. */
export const WEB_HOME_PANEL = 'task-manager.panel';

export const DESKTOP_DOWNLOAD_URL = 'https://apiboo.com/download';
